import { useState, useEffect } from 'react';
import { useMeetingNotifications } from '@/hooks/useMeetingNotifications';
import { MeetingReminderModal } from './MeetingReminderModal';
import { safeStorageJson } from '@/lib/safeJson';

interface MeetingNotificationProviderProps {
  children: React.ReactNode;
}

export function MeetingNotificationProvider({ children }: MeetingNotificationProviderProps) {
  const { notifications, markAsRead } = useMeetingNotifications();
  const [currentMeeting, setCurrentMeeting] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);
  const [notifiedMeetings, setNotifiedMeetings] = useState<Set<string>>(
    // Load from localStorage (tolerante a valor corrompido)
    () => new Set(safeStorageJson<string[]>('notified_meetings', []))
  );

  useEffect(() => {
    // Clean up old notified meetings (older than 24 hours)
    const cleanupOldNotifications = () => {
      const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
      const cleaned = Array.from(notifiedMeetings).filter(id => {
        const timestamp = parseInt(id.split('_')[1] || '0');
        return timestamp > oneDayAgo;
      });
      const newSet = new Set(cleaned);
      setNotifiedMeetings(newSet);
      localStorage.setItem('notified_meetings', JSON.stringify(Array.from(newSet)));
    };

    cleanupOldNotifications();
    const interval = setInterval(cleanupOldNotifications, 60 * 60 * 1000); // Every hour

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (notifications.length > 0 && !showModal) {
      // Find the first meeting that hasn't been notified yet
      const unnotifiedMeeting = notifications.find(
        meeting => !notifiedMeetings.has(meeting.id)
      );

      if (unnotifiedMeeting) {
        setCurrentMeeting(unnotifiedMeeting);
        setShowModal(true);
        
        // Mark as notified
        const newNotified = new Set(notifiedMeetings);
        newNotified.add(unnotifiedMeeting.id);
        setNotifiedMeetings(newNotified);
        localStorage.setItem('notified_meetings', JSON.stringify(Array.from(newNotified)));
      }
    }
  }, [notifications, showModal, notifiedMeetings]);

  const handleClose = () => {
    if (currentMeeting) {
      markAsRead(currentMeeting.id);
    }
    setShowModal(false);
    setCurrentMeeting(null);
  };

  return (
    <>
      {children}
      <MeetingReminderModal
        isOpen={showModal}
        onClose={handleClose}
        meeting={currentMeeting}
      />
    </>
  );
}
